import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment";
import { z } from "zod";

import { FormColumns } from "components/form/FormColumns/FormColumns";
import { FormField } from "components/form/FormField/FormField";
import { CustomInput } from "components/helpers/CustomInput/CustomInput";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { PatientAutocomplete } from "components/ui/PatientAutocomplete/PatientAutocomplete";
import { Select } from "components/ui/Select/Select";
import { useDocumentTypes } from "features/documents/hooks/useDocumentTypes";
import { useUpdateDocument } from "features/documents/hooks/useUpdateDocument";
import { useStaffMembers } from "features/practice/hooks/useStaffMembers";
import { TaskPriority } from "utils/Constant";
import { formatPatientWithDateOfBirth } from "utils/dateFormatter";
import { onInvalidSubmit } from "utils/reportFormErrors";

import styles from "./EditDocumentModal.module.css";

const statusOptions = [
  { value: "NEW", label: "New" },
  { value: "REFERRED", label: "Referred" },
  { value: "IN_REVIEW", label: "In Review" },
  { value: "REVIEWED", label: "Reviewed" },
  { value: "SYSTEM_GENERATED", label: "System Generated" },
];

const formatTypeLabel = (name) => {
  if (!name) return "";
  return name
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formSchema = z.object({
  name: z.string().min(1, "Document name is required"),
  documentType: z
    .string()
    .refine((val) => val !== "" && val !== "undefined", "Document type is required"),
  otherDocumentType: z.string().optional(),
  status: z.string().min(1, "Status is required"),
  staffMemberId: z.string().optional(),
  priority: z
    .string()
    .refine(
      (val) => val !== "-1" && val !== "" && val !== "undefined",
      "Priority is required"
    ),
});

export const EditDocumentModal = ({
  isOpen,
  onClose,
  document,
  patient,
  onSuccess,
  allowPatientSelection = false,
}) => {
  const [selectedPatient, setSelectedPatient] = useState(null);

  const { data: documentTypes = [], isLoading: isLoadingTypes } = useDocumentTypes();
  const { data: staffMembers = [] } = useStaffMembers();
  const updateDocument = useUpdateDocument({ onSuccess });

  const getDefaultValues = () => ({
    name: document?.name || "",
    documentType: document?.documentTypeId?.toString() || "",
    otherDocumentType: document?.otherDocumentType || "",
    status: document?.status || "NEW",
    staffMemberId:
      document?.reviewedById?.toString() ||
      document?.reviewerStaffId?.toString() ||
      patient?.doctorDetails?.id?.toString() ||
      "",
    priority:
      document?.priority !== null && document?.priority !== undefined
        ? String(document.priority)
        : "1",
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    if (isOpen) {
      form.reset(getDefaultValues());

      const patientData = document?.patient || patient;
      if (patientData) {
        setSelectedPatient({
          id: patientData.id,
          title: `${patientData.firstName || ""}${
            patientData.surname ? " " + patientData.surname : ""
          }${
            patientData.dateOfBirth
              ? ` (${moment(patientData.dateOfBirth).format("DD-MM-YYYY")})`
              : ""
          }`.trim(),
        });
      } else {
        setSelectedPatient(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, document, patient]);

  const handleClose = () => {
    form.reset();
    setSelectedPatient(null);
    onClose();
  };

  const onSubmit = (values) => {
    const reviewerStaffId = values.staffMemberId
      ? parseInt(values.staffMemberId)
      : null;

    const payload = {
      ...document,
      id: document.id,
      name: values.name,
      documentTypeId: values.documentType ? parseInt(values.documentType) : null,
      otherDocumentType: values.otherDocumentType || null,
      status: values.status,
      reviewerStaffId: reviewerStaffId,
      reviewedById: reviewerStaffId,
      priority: values.priority ? parseInt(values.priority) : null,
      patientId:
        allowPatientSelection && selectedPatient
          ? selectedPatient.id
          : document?.patientId || patient?.id,
    };

    updateDocument.mutate(payload, { onSuccess: handleClose });
  };

  const handleSubmit = form.handleSubmit(onSubmit, onInvalidSubmit("EditDocumentModal"));
  const watchedDocumentType = form.watch("documentType");
  const showOtherType = watchedDocumentType === "Other";

  const patientDisplayValue = formatPatientWithDateOfBirth(patient);

  return (
    <Dialog open={isOpen} onClose={handleClose} title="Edit document" width={780}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <FormColumns columns={2} gap={24}>
            <FormField name="name" label="Document name">
              <CustomInput {...form.register("name")} />
            </FormField>
            <FormField name="documentType" label="Type">
              <Controller
                name="documentType"
                control={form.control}
                render={({ field }) => (
                  <Select
                    options={documentTypes.map((dt) => ({
                      label: formatTypeLabel(dt.name),
                      value: String(dt.id ?? ""),
                    }))}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      if (value !== "Other") {
                        form.setValue("otherDocumentType", "");
                      }
                    }}
                    placeholder={isLoadingTypes ? "Loading..." : "Select"}
                    disabled={isLoadingTypes}
                    fullWidth
                  />
                )}
              />
            </FormField>
          </FormColumns>

          {showOtherType && (
            <FormField name="otherDocumentType" label="Other document type">
              <CustomInput
                {...form.register("otherDocumentType")}
                placeholder="Enter document type"
              />
            </FormField>
          )}

          <FormColumns columns={2} gap={24}>
            <FormField name="status" label="Status">
              <Controller
                name="status"
                control={form.control}
                render={({ field }) => (
                  <Select
                    options={statusOptions}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select"
                    fullWidth
                  />
                )}
              />
            </FormField>
            <FormField name="priority" label="Priority">
              <Controller
                name="priority"
                control={form.control}
                render={({ field }) => (
                  <Select
                    options={TaskPriority.map((p) => ({
                      label: p.name,
                      value: String(p.value),
                    }))}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select"
                    fullWidth
                  />
                )}
              />
            </FormField>
          </FormColumns>

          <FormColumns columns={2} gap={24}>
            <FormField name="staffMemberId" label="Reviewer">
              <Controller
                name="staffMemberId"
                control={form.control}
                render={({ field }) => (
                  <Select
                    options={staffMembers.map((staff) => ({
                      label: `${staff.active ? "✅ " : ""}${staff.firstName} ${staff.lastName}${staff.staffRole ? ` (${staff.staffRole.toLowerCase()})` : ""}`,
                      value: String(staff.id),
                    }))}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select"
                    fullWidth
                  />
                )}
              />
            </FormField>
            <FormField name="patient" label="Patient">
              {allowPatientSelection ? (
                <PatientAutocomplete
                  id="patient-select"
                  value={selectedPatient}
                  onChange={setSelectedPatient}
                  placeholder="Search patient..."
                />
              ) : (
                <CustomInput value={patientDisplayValue} disabled />
              )}
            </FormField>
          </FormColumns>
        </form>
      </FormProvider>
      <DialogFooter>
        <Button
          variant="outline"
          onClick={handleClose}
          disabled={updateDocument.isPending}
        >
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={updateDocument.isPending}>
          {updateDocument.isPending ? "Saving..." : "Update"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
